// Which two-factor methods the signing-in user actually has.
//
// better-auth's sign-in response already tells us this (`twoFactorMethods`):
// ["totp","otp"] for someone with a verified authenticator app, ["otp"] for
// someone who enrolled without one. We capture it in the twoFactorClient's
// `onTwoFactorRedirect` callback and read it back on /auth/verify-2fa so that
// screen can default to the method the user can actually satisfy.
//
// sessionStorage (not memory) because the hand-off is a full page navigation.
// This is not a secret — it only decides which verification UI to show — and it
// survives a refresh, which is exactly what we want on the verify screen.

const KEY = "cruize.2fa-methods";

export type TwoFactorMethod = "totp" | "otp";

export function stashTwoFactorMethods(methods: unknown): void {
  if (typeof window === "undefined") return;
  if (!Array.isArray(methods)) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(methods));
  } catch {
    // Non-fatal: the verify screen falls back to showing every method.
  }
}

/**
 * Returns the stashed methods, or null when unknown (direct navigation, a new
 * tab, or storage unavailable). Callers must treat null as "show everything"
 * rather than assuming any particular method is missing.
 */
export function readTwoFactorMethods(): TwoFactorMethod[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const methods = parsed.filter((m): m is TwoFactorMethod => m === "totp" || m === "otp");
    return methods.length > 0 ? methods : null;
  } catch {
    return null;
  }
}

export function clearTwoFactorMethods(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Non-fatal.
  }
}
